import {
  CheckboxField,
  SelectField,
  SwitchField,
  TextField,
} from '~/common/components';

import {
  Col,
  Flex,
  Icon,
  Label,
  Row,
  Tooltip,
  useConfiguredForm,
} from '@wyz/ui';
import { SUPPLIERS_TYPES_IDS } from '@wyz/utils';
import { memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans } from '~/common/context';
import { useManageAlternativeSupplierRankingModel } from '~/connect/api';
import { SUPPLIERS_STATUSES } from '~/connect/schemas';
import { ISupplierTemplateForm } from '~/connect/types';

const DISABLED_BETTER_DELAY = [0];
const ENABLED_RANKING_MODEL_ACTIVE = 'ACTIVE';
interface Props {
  baseName:
    | `suppliers.${number}`
    | `bestSourcingSupplierConfigurations.${string}`;
  supplier: {
    id: number;
    type: number;
  };
  index: number;
  enableShowIfBetterDelay?: boolean;
}

const SupplierDraggableItem = ({
  baseName,
  supplier,
  index,
  enableShowIfBetterDelay = true,
}: Props) => {
  const methods = useFormContext<ISupplierTemplateForm>();
  const configurationContext = useConfiguredForm();
  const extendedManufEnabled = methods.watch(
    `${baseName}.extendedManufConfiguration.enabled`,
  );

  const manageSupplierRankingModel = useManageAlternativeSupplierRankingModel({
    list: {
      enabled: true,
      args: {
        query: {
          status: ENABLED_RANKING_MODEL_ACTIVE,
        },
      },
    },
  });

  return (
    <>
      <Row>
        <Col>
          <Label htmlFor={`${baseName}.enabled`} className='capitalize mb-2'>
            <Trans i18nKey='app.supplier_template.form.supplier.active' />
          </Label>
        </Col>
        <Col>
          <SwitchField
            onChangeValue={(enabled) => {
              methods.setValue(
                `${baseName}.status`,
                enabled
                  ? SUPPLIERS_STATUSES.ENABLED
                  : SUPPLIERS_STATUSES.DISABLED,
              );
            }}
            name={`${baseName}.enabled`}
            disabled={configurationContext.mode === 'READ_ONLY'}
            label={''}
            variant='SINGLE'
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Label htmlFor={`${baseName}.orderable`} className='capitalize mb-2'>
            <Trans i18nKey='app.common.status.orderable' />
          </Label>
        </Col>
        <Col>
          <SwitchField
            disabled={configurationContext.mode === 'READ_ONLY'}
            name={`${baseName}.orderable`}
            label={''}
            variant='SINGLE'
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Label
            htmlFor={`${baseName}.childCustomerDelivery`}
            className='capitalize mb-2'
          >
            <Trans i18nKey='app.supplier_template.form.supplier.child_customer_delivery' />
          </Label>
        </Col>
        <Col>
          <SwitchField
            name={`${baseName}.childCustomerDelivery`}
            disabled={configurationContext.mode === 'READ_ONLY'}
            label={''}
            variant='SINGLE'
          />
        </Col>
      </Row>
      {enableShowIfBetterDelay && (
        <Row>
          <CheckboxField
            name={`${baseName}.overridePriorityIfShorterDelivery`}
            label={
              <Flex alignItems='center' className='space-x-2'>
                <span>
                  <Trans i18nKey='app.supplier_template.form.supplier.display' />
                </span>
                {DISABLED_BETTER_DELAY.includes(index) && (
                  <Tooltip
                    label={
                      <Trans i18nKey='app.supplier_template.form.supplier.override_priority_if_shorter_delivery_helper' />
                    }
                  >
                    <Icon icon='info-circle' size='xxs' />
                  </Tooltip>
                )}
              </Flex>
            }
            disabled={DISABLED_BETTER_DELAY.includes(index)}
          />
        </Row>
      )}
      <Row>
        <TextField
          name={`${baseName}.customName`}
          label={<Trans i18nKey='app.common.customer.custom_name' />}
        />
      </Row>
      {supplier.type === SUPPLIERS_TYPES_IDS.WYZ_DISTRIBUTOR && (
        <Row>
          <SelectField
            label={
              <Trans i18nKey='app.supplier_template.form.supplier.ranking_model' />
            }
            name={`${baseName}.rankingModelId`}
            options={
              manageSupplierRankingModel.list.data?.data.map((model) => ({
                value: model.id,
                label: model.name,
              })) ?? []
            }
            isLoading={manageSupplierRankingModel.list.isLoading}
            isClearable
          />
        </Row>
      )}
      {supplier.type === SUPPLIERS_TYPES_IDS.MANUFACTURER && (
        <>
          <Row>
            <Col>
              <Label
                htmlFor={`${supplier.id}-extended-manuf-enabled`}
                className='capitalize mb-2'
              >
                <Trans i18nKey='app.supplier_template.form.supplier.extended_manuf_configuration.enabled' />
              </Label>
            </Col>
            <Col>
              <SwitchField
                onChangeValue={(enabled) => {
                  if (!enabled) {
                    methods.setValue(
                      `${baseName}.extendedManufConfiguration.displayName`,
                      '',
                    );
                    methods.setValue(
                      `${baseName}.extendedManufConfiguration.warehouseId`,
                      '',
                    );
                  }
                }}
                name={`${baseName}.extendedManufConfiguration.enabled`}
                disabled={configurationContext.mode === 'READ_ONLY'}
                label={''}
                variant='SINGLE'
              />
            </Col>
          </Row>
          {extendedManufEnabled && (
            <>
              <TextField
                name={`${baseName}.extendedManufConfiguration.warehouseId`}
                label={
                  <Trans i18nKey='app.supplier_template.form.supplier.extended_manuf_configuration.warehouse_id' />
                }
                disabled={configurationContext.mode === 'READ_ONLY'}
                required
              />
              <TextField
                name={`${baseName}.extendedManufConfiguration.displayName`}
                label={
                  <Trans i18nKey='app.supplier_template.form.supplier.extended_manuf_configuration.display_name' />
                }
                disabled={configurationContext.mode === 'READ_ONLY'}
                required
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default memo(SupplierDraggableItem);
